import { supabase } from '../../lib/supabase';
import { ChatMessage } from '../../features/chat/hooks/types';
import { ChatMessageMapper } from '../../infrastructure/mappers/ChatMessageMapper';
import { notifyPartykitLocalMessage } from '../../features/chat/utils/partykitSocket';
import { isAbortError } from '../../utils/networkError';

export class ChatMessageRepository {
    /**
     * Salva uma mensagem no banco de dados
     */
    async createMessage(
        conversationDbId: string,
        message: ChatMessage,
        organizationId?: string
    ): Promise<void> {
        try {
            // Tenta usar RPC atômico (Recomendado)
            const rpcParams = {
                p_conversation_id: conversationDbId,
                p_message_id: message.id,
                p_content: message.text || '',
                p_message_type: message.type,
                p_sender: message.sender,
                p_timestamp: message.timestamp,
                p_organization_id: organizationId || null,
                p_media_url: message.media?.url || null,
                p_transcription: message.media?.transcription || null,
                p_quoted_message_id: message.quotedMessage?.id || null,
                p_quoted_content: message.quotedMessage?.content || null,
                p_contact_id: null // Opcional
            };

            const { data, error: rpcError } = await supabase.rpc('create_message_atomic', rpcParams);

            if (!rpcError) {
                if (data && data.created === false && data.reason === 'already_exists') {
                    if (import.meta.env.DEV) console.debug('[ChatMessageRepo] Mensagem já existe (RPC).');
                    return;
                }
                this.notifyPeers(conversationDbId, message, organizationId);
                return;
            }

            // Fallback: Se RPC falhar (ex: não existe), usa método legado
            if (rpcError.code !== '42883') { // 42883 = function does not exist
                throw rpcError;
            }

            // Verifica duplicação no fallback
            const { data: existing } = await supabase
                .from('chat_messages')
                .select('id')
                .eq('message_id', message.id)
                .eq('conversation_id', conversationDbId)
                .maybeSingle();

            if (existing) return;

            const messageData = {
                ...ChatMessageMapper.toDatabase(message, conversationDbId),
                ...(organizationId ? { organization_id: organizationId } : {})
            };

            const { error } = await supabase
                .from('chat_messages')
                .insert(messageData);

            if (error && error.code !== 'PGRST116') throw error;
            this.notifyPeers(conversationDbId, message, organizationId);

        } catch (error: any) {
            console.error('[ChatMessageRepo] Erro ao salvar mensagem:', error);
            throw error;
        }
    }

    /** Outros agentes/abas escutam PartyKit — só com id real (nunca temp-*). */
    private notifyPeers(
        conversationDbId: string,
        message: ChatMessage,
        organizationId?: string,
    ): void {
        if (!organizationId) return;
        if (/^(temp-|sent-)/.test(message.id)) return;
        void notifyPartykitLocalMessage({
            conversationId: conversationDbId,
            organizationId,
            messageId: message.id,
            fromMe: message.sender !== 'them',
            content: message.text,
            messageType: message.type,
            messageTimestamp: message.timestamp,
        });
    }

    /**
     * Busca histórico de mensagens do banco
     * SEGURANÇA: Valida organização via JOIN para evitar cross-org leak
     * 
     * OTIMIZAÇÃO EGRESS:
     * - Limite padrão de 100 mensagens (últimas)
     * - Seleção apenas de campos necessários
     * - Paginação disponível via offset
     */
    async getMessages(
        conversationDbId: string, 
        organizationId?: string,
        limit: number = 150,
        offset: number = 0,
        signal?: AbortSignal,
    ): Promise<ChatMessage[]> {
        try {
            let query = supabase
                .from('chat_messages')
                .select(`
                    id,
                    message_id,
                    content,
                    message_type,
                    sender,
                    timestamp,
                    status,
                    media_url,
                    transcription,
                    quoted_message_id,
                    quoted_content,
                    quoted_media_url,
                    quoted_type,
                    quoted_sender,
                    reactions,
                    is_edited,
                    ad_metadata,
                    raw_data
                `)
                .eq('conversation_id', conversationDbId)
                .order('timestamp', { ascending: false })
                .order('created_at', { ascending: false })
                .range(offset, offset + limit - 1);

            // Filtra por organization_id diretamente (sem inner join que causa RLS issues)
            if (organizationId) {
                query = query.eq('organization_id', organizationId);
            }

            if (signal) query = query.abortSignal(signal);
            const { data, error } = await query;

            if (error) {
                if (signal?.aborted || isAbortError(error)) return [];
                throw error;
            }

            // Reverte a ordem para mostrar do mais antigo ao mais novo
            const messages = (data || []).reverse();
            return messages.map(ChatMessageMapper.toDomain);
        } catch (error) {
            if (signal?.aborted || isAbortError(error)) return [];
            console.error('[ChatMessageRepo] Erro ao buscar histórico:', error);
            throw error;
        }
    }

    /**
     * Busca mensagens mais antigas (para scroll infinito)
     */
    async getOlderMessages(
        conversationDbId: string,
        beforeTimestamp: Date,
        organizationId?: string,
        limit: number = 50
    ): Promise<ChatMessage[]> {
        try {
            let query = supabase
                .from('chat_messages')
                .select(`
                    id,
                    message_id,
                    content,
                    message_type,
                    sender,
                    timestamp,
                    status,
                    media_url,
                    transcription,
                    quoted_message_id,
                    quoted_content,
                    quoted_media_url,
                    quoted_type,
                    quoted_sender,
                    reactions,
                    is_edited,
                    ad_metadata,
                    raw_data
                `)
                .eq('conversation_id', conversationDbId)
                .lt('timestamp', beforeTimestamp.toISOString())
                .order('timestamp', { ascending: false })
                .order('created_at', { ascending: false })
                .limit(limit);

            if (organizationId) {
                query = query.eq('organization_id', organizationId);
            }

            const { data, error } = await query;

            if (error) {
                if (isAbortError(error)) return [];
                throw error;
            }

            return (data || []).reverse().map(ChatMessageMapper.toDomain);
        } catch (error) {
            if (isAbortError(error)) return [];
            console.error('[ChatMessageRepo] Erro ao buscar mensagens antigas:', error);
            throw error;
        }
    }
}
